import {scrollToElement} from "./scrollTo";
import {validateRegisterFormWhileCreatingContract} from "./registerFormValidation";
import {defaultUserFormForContractCreating} from "../pages/Auth/RegisterDefaultValues";
import {highlightPaymentUrl} from "./scrollTo";
import { getParagraphSelectedValue } from "./workWithParagraphEntity";
import { getObjectSelectedValue } from "./workWithObjectEntity";
import {BOOL_ENTITY_TYPE, isBoolEntityEmpty} from "./boolEntityField";
import {isEntityVisible} from "./entityVisibilityCondition";
import {isEntityMarkerInHtml} from "./entityMarkerInHtml";
import {MissingDataError} from "./missingDataError";

/** Абзац: пусто по trim или значение не из списка вариантов (в т.ч. произвольный текст до blur). */
function isParagraphSelectionMissingOrInvalid(entity: any): boolean {
    const s = (entity.value ?? "").toString().trim();
    if (s.length === 0) return true;
    return getParagraphSelectedValue(entity) == null;
}

/** Объект: пусто по trim или id не совпал ни с одним объектом в шаблоне (strict select, пробелы, несовпадение). */
function isObjectSelectionMissingOrInvalid(entity: any): boolean {
    const s = (entity.value ?? "").toString().trim();
    if (s.length === 0) return true;
    return getObjectSelectedValue(entity) == null;
}

export function validatePaymentUrl(contract: any): { isValid: boolean, error?: string } {
    if (contract.settings?.payment_url?.enabled) {
        if (!contract.settings?.payment_url?.url) {
            highlightPaymentUrl();
            return { isValid: false, error: "Для платежной ссылки необходимо указать URL" };
        }

        // Проверяем валидность URL
        try {
            let url = new URL(contract.settings.payment_url.url);
        } catch (e) {
            highlightPaymentUrl();
            return { isValid: false, error: "Указан некорректный URL" };
        }
    }
    return { isValid: true };
}

function checkParagraphEntities(contract: any): { emptyParagraphs: string[], emptyInnerFields: string[] } {
    const emptyParagraphs: string[] = [];
    const emptyInnerFields: string[] = [];

    contract.entities
        .filter((entity: any) => entity.type === "Абзац")
        .forEach((entity: any) => {
            // Проверяем сам абзац
            if (isEntityVisible(entity, contract.entities) &&
                ((!("isRequired" in entity) || entity["isRequired"] === true) &&
                !entity.clientCanFill &&
                isParagraphSelectionMissingOrInvalid(entity))) {
                emptyParagraphs.push(entity.keyword);
            }

            // Проверяем внутренние поля
            const selectedValue = getParagraphSelectedValue(entity);
            if (selectedValue) {
                selectedValue.entities.forEach((innerEntity: any) => {
                    if (isRequiredInnerFillableEmpty(innerEntity, false)) {
                        emptyInnerFields.push(`${entity.keyword}.${innerEntity.keyword}`);
                    }
                });
            }
        });

    return { emptyParagraphs, emptyInnerFields };
}

// Функция для проверки полей типа "Абзац" без исключения clientCanFill
function checkParagraphEntitiesNoSignatureRequired(contract: any): { emptyParagraphs: string[], emptyInnerFields: string[] } {
    const emptyParagraphs: string[] = [];
    const emptyInnerFields: string[] = [];

    contract.entities
        .filter((entity: any) => entity.type === "Абзац")
        .forEach((entity: any) => {
            // Проверяем сам абзац (включая clientCanFill)
            if ((!("isRequired" in entity) || entity["isRequired"] === true) &&
                isParagraphSelectionMissingOrInvalid(entity)) {
                emptyParagraphs.push(entity.keyword);
            }

            // Проверяем внутренние поля (включая clientCanFill)
            const selectedValue = getParagraphSelectedValue(entity);
            if (selectedValue) {
                selectedValue.entities.forEach((innerEntity: any) => {
                    if (isRequiredInnerFillableEmpty(innerEntity, true)) {
                        emptyInnerFields.push(`${entity.keyword}.${innerEntity.keyword}`);
                    }
                });
            }
        });

    return { emptyParagraphs, emptyInnerFields };
}

function checkObjectEntities(contract: any): { emptyObjects: string[], emptyInnerFields: string[] } {
    const emptyObjects: string[] = [];
    const emptyInnerFields: string[] = [];

    contract.entities
        .filter((entity: any) => entity.type === "Объект")
        .forEach((entity: any) => {
            // Проверяем сам объект
            if (isEntityVisible(entity, contract.entities) &&
                ((!("isRequired" in entity) || entity["isRequired"] === true) &&
                !entity.clientCanFill &&
                isObjectSelectionMissingOrInvalid(entity))) {
                emptyObjects.push(entity.keyword);
            }

            // Проверяем внутренние поля
            const selectedValue = getObjectSelectedValue(entity);
            if (selectedValue) {
                selectedValue.entities.forEach((innerEntity: any) => {
                    if (isRequiredInnerFillableEmpty(innerEntity, false)) {
                        emptyInnerFields.push(`${entity.keyword}.${innerEntity.keyword}`);
                    }
                });
            }
        });

    return { emptyObjects, emptyInnerFields };
}

// Функция для проверки полей типа "Объект" без исключения clientCanFill
function checkObjectEntitiesNoSignatureRequired(contract: any): { emptyObjects: string[], emptyInnerFields: string[] } {
    const emptyObjects: string[] = [];
    const emptyInnerFields: string[] = [];

    contract.entities
        .filter((entity: any) => entity.type === "Объект")
        .forEach((entity: any) => {
            // Проверяем сам объект (включая clientCanFill)
            if ((!("isRequired" in entity) || entity["isRequired"] === true) &&
                isObjectSelectionMissingOrInvalid(entity)) {
                emptyObjects.push(entity.keyword);
            }

            // Проверяем внутренние поля (включая clientCanFill)
            const selectedValue = getObjectSelectedValue(entity);
            if (selectedValue) {
                selectedValue.entities.forEach((innerEntity: any) => {
                    if (isRequiredInnerFillableEmpty(innerEntity, true)) {
                        emptyInnerFields.push(`${entity.keyword}.${innerEntity.keyword}`);
                    }
                });
            }
        });

    return { emptyObjects, emptyInnerFields };
}

export function validateContractBeforeSave(contract: any, shouldSign: boolean | null) {
    let missing = missingEntities(contract)
    if (missing.length > 0) {
        let kw = `copyView.${missing[0]}`;
        scrollToElement(kw, true)
        throw new Error(
            `Метка Поля "${missing[0]}" должна быть в тексте договора. Скопируйте метку и разместите ее в тексте договора`,
        );
    }

    let allEmptyEntities = emptyEntities(contract)
    if (allEmptyEntities.length > 0) {
        const n = allEmptyEntities.length
        const listed = allEmptyEntities.slice(0, 4).join(", ")
        const more = n > 4 ? ` и ещё ${n - 4}` : ""
        const n10 = n % 10
        const n100 = n % 100
        const fieldsWord =
            n10 === 1 && n100 !== 11
                ? "обязательное поле"
                : n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)
                    ? "обязательных поля"
                    : "обязательных полей"
        throwMissingData(
            n === 1
                ? `Заполните обязательное поле «${allEmptyEntities[0]}»`
                : `Заполните ${n} ${fieldsWord}: ${listed}${more}`,
            allEmptyEntities,
        )
    }

    throwIfEmptyComputedFormula(contract, false)

    // Проверяем поля типа "Абзац"
    const { emptyParagraphs, emptyInnerFields: emptyParagraphInnerFields } = checkParagraphEntities(contract);
    if (emptyParagraphs.length > 0) {
        throwMissingData(
            `Необходимо выбрать шаблон для абзаца "${emptyParagraphs[0]}"`,
            emptyParagraphs,
        );
    }
    if (emptyParagraphInnerFields.length > 0) {
        throwMissingData(
            `Поле "${emptyParagraphInnerFields[0]}" должно быть заполнено`,
            emptyParagraphInnerFields,
        );
    }

    // Проверяем поля типа "Объект"
    const { emptyObjects, emptyInnerFields: emptyObjectInnerFields } = checkObjectEntities(contract);
    if (emptyObjects.length > 0) {
        throwMissingData(
            `Необходимо выбрать объект для поля "${emptyObjects[0]}"`,
            emptyObjects,
        );
    }
    if (emptyObjectInnerFields.length > 0) {
        throwMissingData(
            `Поле "${emptyObjectInnerFields[0]}" должно быть заполнено`,
            emptyObjectInnerFields,
        );
    }

    let userFormErrors = validateRegisterFormWhileCreatingContract(
        contract.system_entities,
        defaultUserFormForContractCreating(contract)
    )
    if (userFormErrors.length > 0) {
        throw new MissingDataError(userFormErrors.join(".\n\n"))
    }
}

function isSimpleEntityEmpty(entity: any): boolean {
    if (entity.type === BOOL_ENTITY_TYPE) {
        return isBoolEntityEmpty(entity);
    }
    return (entity.value?.toString() ?? "").length == 0;
}

function isRequiredEntity(entity: any): boolean {
    return !("isRequired" in entity) || entity["isRequired"] === true
}

function isRequiredInnerFillableEmpty(innerEntity: any, includeClientFill: boolean): boolean {
    if (innerEntity?.type === "Формула") {
        return false
    }
    if (!isRequiredEntity(innerEntity)) {
        return false
    }
    if (!includeClientFill && innerEntity.clientCanFill) {
        return false
    }
    return !innerEntity.value || innerEntity.value.length === 0
}

const FILLABLE_EMPTY_TYPES = ["Текст", "Дата", "Фото", "Список", BOOL_ENTITY_TYPE]

function throwIfEmptyComputedFormula(contract: any, includeClientFill: boolean) {
    const error = computedFormulaFillError(contract, includeClientFill)
    if (!error) {
        return
    }
    throwMissingData(error.message, error.keywords)
}

function throwMissingData(message: string, keywords: string[]) {
    if (keywords[0]) {
        scrollToElement(`input.${keywords[0]}`, false)
    }
    throw new MissingDataError(message, keywords)
}

export function computedFormulaFillError(
    contract: any,
    includeClientFill = false,
): { scrollKeyword: string, message: string, keywords: string[] } | null {
    const formulaEntity = (contract.entities ?? []).find((entity: any) =>
        isEntityVisible(entity, contract.entities) &&
        entity.type === "Формула" &&
        isRequiredEntity(entity) &&
        (includeClientFill || !entity.clientCanFill) &&
        isSimpleEntityEmpty(entity),
    )
    if (!formulaEntity) {
        return null
    }

    const emptySources = emptyFormulaSourceFields(contract, formulaEntity)
    const emptyDates = emptySources.filter((entity: any) => entity.type === "Дата")
    if (emptyDates.length > 0) {
        const keywords = emptyDates.map((entity: any) => entity.keyword)
        return {
            scrollKeyword: keywords[0],
            keywords,
            message: `Укажите даты — «${formulaEntity.keyword}» посчитается само`,
        }
    }
    if (emptySources.length > 0) {
        const keywords = emptySources.map((entity: any) => entity.keyword)
        return {
            scrollKeyword: keywords[0],
            keywords,
            message: `Заполните «${emptySources[0].keyword}» — «${formulaEntity.keyword}» посчитается само`,
        }
    }
    return {
        scrollKeyword: formulaEntity.keyword,
        keywords: [formulaEntity.keyword],
        message: `«${formulaEntity.keyword}» считается автоматически. Заполните поля, от которых оно зависит.`,
    }
}

function emptyFormulaSourceFields(contract: any, formulaEntity: any, visited: Set<string> = new Set()): any[] {
    const formulaKeyword = (formulaEntity.keyword ?? "").toLowerCase()
    if (!formulaKeyword || visited.has(formulaKeyword)) {
        return []
    }
    visited.add(formulaKeyword)

    const keywords: string[] = []
    const formula = (formulaEntity.formula ?? "").toString()
    const brace = /\{\{([^}]+)\}\}/g
    let match: RegExpExecArray | null
    while ((match = brace.exec(formula)) !== null) {
        const keyword = match[1].trim()
        if (keyword) {
            keywords.push(keyword)
        }
    }
    formula.replace(/\{\{[^}]+\}\}/g, "").split(/[+\-*/()]/).forEach((token: string) => {
        const keyword = token.trim()
        if (!keyword) {
            return
        }
        const numeric = keyword.replace(",", ".").replace(/ /g, "")
        if (numeric.length > 0 && !isNaN(Number(numeric))) {
            return
        }
        keywords.push(keyword)
    })

    const sources: any[] = []
    const seen = new Set<string>()
    for (const keyword of keywords) {
        const key = keyword.toLowerCase()
        if (!key || seen.has(key) || key === formulaKeyword) {
            continue
        }
        seen.add(key)
        const entity = (contract.entities ?? []).find(
            (item: any) => (item.keyword ?? "").toLowerCase() === key,
        )
        if (!entity || !isSimpleEntityEmpty(entity)) {
            continue
        }
        if (entity.type === "Формула") {
            sources.push(...emptyFormulaSourceFields(contract, entity, visited))
            continue
        }
        sources.push(entity)
    }
    return sources
}

export function emptyEntities(contract: any): string[] {
    return contract.entities
        .filter((entity: any) => isEntityVisible(entity, contract.entities))
        .filter((entity: any) => isRequiredEntity(entity))
        .filter(
            (entity: any) =>
                (FILLABLE_EMPTY_TYPES.includes(entity.type) || !entity.type) &&
                !entity.clientCanFill &&
                isSimpleEntityEmpty(entity),
        )
        .map((entity: any) => { return entity.keyword })
}

// Специальная функция для документов без требования подписи клиента
// Проверяет ВСЕ поля, включая те, которые должен заполнять клиент
export function emptyEntitiesForNoSignatureRequired(contract: any): string[] {
    return contract.entities
        .filter((entity: any) => isEntityVisible(entity, contract.entities))
        .filter((entity: any) => isRequiredEntity(entity))
        .filter(
            (entity: any) =>
                (FILLABLE_EMPTY_TYPES.includes(entity.type) || !entity.type) &&
                isSimpleEntityEmpty(entity),
        )
        .map((entity: any) => { return entity.keyword })
}

/**
 * Все незаполненные обязательные поля, которые должен заполнить владелец
 * (исключая поля для клиента). Используется для предупреждения перед созданием
 * многоразовой ссылки. Включает простые поля, абзацы и объекты.
 */
export function emptyOwnerEntities(contract: any): string[] {
    if (!contract?.entities) return [];

    const simple = emptyEntities(contract);
    const { emptyParagraphs, emptyInnerFields: emptyParagraphInner } = checkParagraphEntities(contract);
    const { emptyObjects, emptyInnerFields: emptyObjectInner } = checkObjectEntities(contract);

    return Array.from(new Set([
        ...simple,
        ...emptyParagraphs,
        ...emptyParagraphInner,
        ...emptyObjects,
        ...emptyObjectInner,
    ]));
}

export function missingEntities(contract: any): string[] {
    return contract.entities
        .filter((entity: any) => (entity.isRequired ?? true))
        // «Таблица» заполняется через API; метка могла быть съедена старой подстановкой HTML.
        // Не блокируем сохранение правок обычного текста из‑за отсутствия <<keyword>>.
        .filter((entity: any) => entity.type !== "Объект" && entity.type !== "PDF" && entity.type !== "Таблица")
        .filter((entity: any) => !isEntityInText(entity.keyword, contract.body))
        .map((entity: any) => { return entity.keyword })
}

export function getEntityPatterns(keyword: string) {
    return {
        normalPattern: `<<${keyword}>>`,
        escapedPattern: `&lt;&lt;${keyword}&gt;&gt;`,
        rawPattern: `{{${keyword}}}`
    };
}

export function isEntityInText(keyword: string, text: string, checkRaw: boolean = false): boolean {
    return isEntityMarkerInHtml(keyword, text ?? '', checkRaw)
}

// Специальная функция валидации для документов без требования подписи клиента
export function validateContractBeforeSaveNoSignatureRequired(contract: any) {
    let missing = missingEntities(contract)
    if (missing.length > 0) {
        let kw = `copyView.${missing[0]}`;
        scrollToElement(kw, true)
        throw new Error(
            `Метка Поля "${missing[0]}" должна быть в тексте договора. Скопируйте метку и разместите ее в тексте договора`,
        );
    }

    // Проверяем ВСЕ поля, включая те, которые должен заполнять клиент
    let allEmptyEntities = emptyEntitiesForNoSignatureRequired(contract)
    if (allEmptyEntities.length > 0) {
        throwMissingData(
            `Поле "${allEmptyEntities[0]}" должно быть заполнено (включая поля для клиента)`,
            allEmptyEntities,
        );
    }

    throwIfEmptyComputedFormula(contract, true)

    // Проверяем поля типа "Абзац" (включая clientCanFill)
    const { emptyParagraphs, emptyInnerFields: emptyParagraphInnerFields } = checkParagraphEntitiesNoSignatureRequired(contract);
    if (emptyParagraphs.length > 0) {
        throwMissingData(
            `Необходимо выбрать шаблон для абзаца "${emptyParagraphs[0]}" (включая поля для клиента)`,
            emptyParagraphs,
        );
    }
    if (emptyParagraphInnerFields.length > 0) {
        throwMissingData(
            `Поле "${emptyParagraphInnerFields[0]}" должно быть заполнено (включая поля для клиента)`,
            emptyParagraphInnerFields,
        );
    }

    // Проверяем поля типа "Объект" (включая clientCanFill)
    const { emptyObjects, emptyInnerFields: emptyObjectInnerFields } = checkObjectEntitiesNoSignatureRequired(contract);
    if (emptyObjects.length > 0) {
        throwMissingData(
            `Необходимо выбрать объект для поля "${emptyObjects[0]}" (включая поля для клиента)`,
            emptyObjects,
        );
    }
    if (emptyObjectInnerFields.length > 0) {
        throwMissingData(
            `Поле "${emptyObjectInnerFields[0]}" должно быть заполнено (включая поля для клиента)`,
            emptyObjectInnerFields,
        );
    }

    let userFormErrors = validateRegisterFormWhileCreatingContract(
        contract.system_entities,
        defaultUserFormForContractCreating(contract)
    )
    if (userFormErrors.length > 0) {
        throw new MissingDataError(userFormErrors.join(".\n\n"))
    }
}